import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Pagination, Row, Col, Form, Dropdown, DropdownButton, Card } from 'react-bootstrap';

import Loading from '../../../components/Loading/Loading';
import CheckBox from '../../../components/CheckBox/CheckBox';
import throttle from '../../../utils/throttle';
import pagination from '../../../utils/pagination';
import { PRICES, getModel } from '../../../utils/aiUtils';

import { getAiStats } from '../AiStatsActions';
import { getAiPrompts } from '../AiToolsActions';

import '../../../assets/styles/_table.scss';
import './AccountsStatsTable.scss';

class AccountsStatsTable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      page: 1,
      limit: 10,
      search: '',
      selected: {},
      sort: '-name',
      sortOptions: [
        {
          name: 'Prompt: A to Z',
          value: 'name',
        },
        {
          name: 'Prompt: Z to A',
          value: '-name',
        },
        {
          name: 'Dashboard: A to Z',
          value: 'dashboard',
        },
        {
          name: 'Dashboard: Z to A',
          value: '-dashboard',
        },
        {
          name: 'Email: A to Z',
          value: 'email',
        },
        {
          name: 'Email: Z to A',
          value: '-email',
        },
        {
          name: 'Tokens: Low to High',
          value: 'tokens',
        },
        {
          name: 'Tokens: High to Low',
          value: '-tokens',
        },
        {
          name: 'Requests: Low to High',
          value: 'requests',
        },
        {
          name: 'Requests: High to Low',
          value: '-requests',
        },
        {
          name: 'Avg. Time: Fast to Slow',
          value: 'avgtime',
        },
        {
          name: 'Avg. Time: Slow to Fast',
          value: '-avgtime',
        },
      ],
      limitOptions: [
        { name: '10', value: 10 },
        { name: '20', value: 20 },
        { name: '50', value: 50 },
        { name: '100', value: 100 },
      ],
      prompt: 'All prompts',
      promptOptions: [],
      results: []
    };

    this.getStatsThrottled = throttle(this.getStats, 500, this);
  }

  componentDidMount() {
    this.props.getAiPrompts({ page: 'all' });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.prompts !== this.props.prompts && this.props.prompts) {
      const promptOptions = this.props.prompts.map(p => ({
        name: p.name,
        value: p.promptHash,
        enabled: true
      }));
      this.setState({
        promptOptions,
        prompt: `${promptOptions.length} prompt${promptOptions.length !== 1 ? 's' : ''}`
      }, () => {
        this.getStats();
      });
    }
    if (prevProps.results !== this.props.results && this.props.results) {
      this.setState({
        results: this.prepareResults(this.props.results)
      })
    }
  }

  prepareResults = (results) => {
    const { prompts } = this.props;
    return _.map(results, data => {
      const prompt = _.find(prompts, p => p.promptHash === data.promptHash);
      const { llmModel } = getModel(prompt.template);
      const pricing = PRICES[llmModel] || PRICES['gpt-4o'];

      const cost = (
        (data.totalInputTokens / 1e6) * pricing.input +
        (data.totalOutputTokens / 1e6) * pricing.output
      );
      const avgCostPerRequest = data.totalRequests > 0 ? cost / data.totalRequests : 0;
      const avgTimePerRequest = data.totalRequests > 0 ? data.totalTime / data.totalRequests : 0;

      return {
        ...data,
        model: llmModel,
        totalCost: cost,
        avgCostPerRequest,
        avgTimePerRequest
      }
    });
  };

  selectAll = ({checked}) => {
    const data = this.props.results;

    const selected = {};
    _.forEach(data, d => {
      selected[d.dashboardId] = !d.hidden && checked;
    });

    this.setState({ selected });
  }

  isAllSelected = () => {
    const { selected } = this.state;
    const data = this.props.results;
    return !_.some(data, d => !selected[d.dashboardId] && !d.hidden);
  }

  renderSelected = (type) => {
    const selected = _.find(this.state[type + 'Options'], {
      value: this.state[type],
    });
    return selected ? selected.name : '';
  };

  handleSearchChange = (ev) => {
    this.setState({ search: ev.target.value, page: 1 });
    this.getStatsThrottled();
  };

  handleSortChange = (sort) => {
    this.setState({ sort, page: 1 }, this.getStats);
  };

  handleLimitChange = (limit) => {
    this.setState({ limit, page: 1 }, this.getStats);
  };

  handlePromptFilterChange = (promptName) => {
    let selectedCount = 0;
    const prompts = _.map(this.state.promptOptions, (p) => {
      if (p.name === promptName) {
        selectedCount += !p.enabled ? 1 : 0; 
        return { ...p, enabled: !p.enabled };
      } else {
        selectedCount += p.enabled ? 1 : 0;
        return p;
      }
    })
    this.setState({
      prompt: `${selectedCount} prompt${selectedCount > 1 ? 's' : ''}`,
      promptOptions: prompts,
      page: 1
    }, this.getStats);
  };

  goto = (page = 1) => {
    if (page < 1) {
      page = 1;
    }
    this.setState({ page }, this.getStats);
  };

  getStats = () => {
    const { promptOptions, sort, search, limit, page } = this.state;
    const filter = _(promptOptions).filter({ enabled: true }).map(p => p.value).value().join(',');
    
    this.props.getAiStats({
      filter,
      search,
      sort,
      page,
      limit,
    });
  };

  formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${Math.floor(seconds) % 60}s`;
    }
    return `${seconds.toFixed(2)} sec`;
  };

  renderSummaryBlock = () => {
    const { results } = this.state;
    
    if (!results || results.length === 0) {
      return null;
    }
    
    const totalCost = _.sumBy(results, 'totalCost');
    const totalRequests = _.sumBy(results, 'totalRequests');
    const totalTime = _.sumBy(results, 'totalTime');
    const avgCostPerRequest = totalRequests > 0 ? totalCost / totalRequests : 0;
    const avgTimePerRequest = totalRequests > 0 ? totalTime / totalRequests : 0;

    return (
      <Card className='summary-block'>
        <Card.Body>
          <div class="card-body__item">
            <label>Total Cost</label>
            <span>${totalCost.toFixed(2)}</span>
          </div>
          <div class="card-body__item">
            <label>Total Requests</label>
            <span>{totalRequests.toLocaleString()}</span>
          </div>
          <div class="card-body__item">
            <label>Total Time</label>
            <span>{this.formatTime(totalTime)}</span>
          </div>
          <div class="card-body__item">
            <label>Avg Cost/Request</label>
            <span>${avgCostPerRequest.toFixed(4)}</span>
          </div>
          <div class="card-body__item">
            <label>Avg Time/Request</label>
            <span>{this.formatTime(avgTimePerRequest)}</span>
          </div>
        </Card.Body>
      </Card>
    );
  };

  renderPagination = () => {
    const { total, page } = this.props;
    const { limit } = this.state;
    const pages = pagination(total, page, limit, 5);

    if (pages.totalPages < 2) {
      return <div></div>;
    }
    return (
      <Pagination>
        {pages.currentPage > 1 && <Pagination.First onClick={() => this.goto(1)} />}
        {pages.currentPage > 1 && <Pagination.Prev onClick={() => this.goto(pages.currentPage - 1)} />}
        {pages.pages.map((p, i) => {
          return (
            <Pagination.Item key={i} active={p === pages.currentPage} onClick={() => this.goto(p)}>
              {p}
            </Pagination.Item>
          );
        })}
        {pages.currentPage < pages.totalPages && <Pagination.Next onClick={() => this.goto(pages.currentPage + 1)} />}
        {pages.currentPage < pages.totalPages && <Pagination.Last onClick={() => this.goto(pages.totalPages)} />}
      </Pagination>
    );
  };

  renderRow = (data, i) => {
    const successRate = data.totalRequests ? Math.round(data.successCount / data.totalRequests * 100) : 'N/A';
    const cacheHitRate = data.totalRequests ? Math.round(data.cacheHits / (data.totalRequests + data.cacheHits) * 100) : 'N/A';
    
    return (
      <Row key={i} className='row-item'>
        {/* Prompt Name */}
        <Col xs={12} md={3} className='col-one'>
          <p>
            <label>Prompt Name:</label>
          </p>
          <p>
            <span>{data.promptName}</span>
          </p>
          <p>
            <label>Model:</label>
          </p>
          <p>
            <span>{data.model}</span>
          </p>
        </Col>

        {/* Account */}
        <Col md={3} className='col-two'>
          <p>
            <label>Account:</label>
          </p>
          <p>
            <span>{data.accountId}</span>
          </p>
          <p>
            <label>Owner:</label>
          </p>
          <p>
            <span>{data.ownerEmail}</span>
          </p>
        </Col>

        {/* Dashboard Name */}
        <Col md={3} className='col-three'>
          <p>
            <label>Dashboard:</label>
          </p>
          <p>
            <span>{data.dashboardId}</span>
          </p>
          <p>
            <label>Name:</label>
          </p>
          <p>
            <span>{data.dashboardName}</span>
          </p>
        </Col>

        {/* Stats */}
        <Col md={3} className='col-four'>
          <p><label>Cache Hit Rate:</label> <span>{cacheHitRate} %</span></p>
          <p><label>Success Rate:</label> <span>{successRate} %</span></p>
          <p><label>Total Cost:</label><span>${data.totalCost.toFixed(2)}</span></p>
          <p><label>Total Requests:</label> <span>{data.totalRequests.toLocaleString()}</span></p>
          <p><label>Total Time:</label> <span>{this.formatTime(data.totalTime)}</span></p>
          <p><label>Avg Cost/Req:</label> <span>${data.avgCostPerRequest.toFixed(4)}</span></p>
          <p><label>Avg Time/Req:</label> <span>{this.formatTime(data.avgTimePerRequest)}</span></p>
        </Col>
      </Row>
    );
  };

  render() {
    const { isLoading, total } = this.props;
    const { results } = this.state;

    return (
      <div className='accounts-stats-wrapper'>
        {this.renderSummaryBlock()}
        <div className='table-content aistats'>
          <div className='table-filter'>
            <div className='table-filter-group'>
              <div className='d-flex gap-2'>
                <Form.Group
                  controlId="bulkSelect"
                  className='bulk-select d-flex align-items-center justify-content-center pr-2'
                >
                  <CheckBox
                    name="isAllChecked"
                    id="isAllChecked"
                    checked={this.isAllSelected()}
                    onChange={this.selectAll}
                  />
                </Form.Group>
                <h2>Account Stats</h2>
              </div>
              
              <div className='d-flex gap-2'>
                <Form.Group controlId="prompt" className="filter-dropdown">
                  <DropdownButton
                    className="form-dropdown"
                    id="dropdown-item-button"
                    title={this.state.prompt}
                  >
                    {this.state.promptOptions.map((f, i) => {
                      return (
                        <Dropdown.Item
                          key={i}
                          as="button"
                        >
                          <CheckBox
                            key={f.name}
                            id={`filter-${f.name}`}
                            label={f.name}
                            checked={f.enabled}
                            onChange={() => this.handlePromptFilterChange(f.name)}
                          />
                        </Dropdown.Item>
                      );
                    })}
                  </DropdownButton>
                </Form.Group>
                <Form.Group controlId="sort" className="white filter-dropdown">
                  <DropdownButton className="form-dropdown sort" id="dropdown-item-button" title={this.renderSelected('sort')}>
                    {this.state.sortOptions.map((s, i) => {
                      return (
                        <Dropdown.Item
                          key={i}
                          as="button"
                          className={ this.state.sort === s.value ? 'active' : '' }
                          onClick={() => this.handleSortChange(s.value)}
                        >
                          {s.name}
                        </Dropdown.Item>
                      );
                    })}
                  </DropdownButton>
                </Form.Group>
                <Form.Group controlId="action" className="filter-dropdown">
                  <DropdownButton
                    className="form-dropdown"
                    id="dropdown-item-button"
                    title={this.renderSelected('limit')}
                  >
                    {this.state.limitOptions.map((f, i) => {
                      return (
                        <Dropdown.Item
                          key={i}
                          as="button"
                          className={this.state.limit === f.value ? 'active' : ''}
                          onClick={() => this.handleLimitChange(f.value)}>
                          {f.name}
                        </Dropdown.Item>
                      );
                    })}
                  </DropdownButton>
                </Form.Group>
                <Form.Group controlId="AccountSearch" className="search-form mr-2">
                  <Form.Control
                    type="text"
                    name="search"
                    placeholder="Search by Account"
                    onChange={this.handleSearchChange}
                    value={this.state.search}
                  />
                </Form.Group>
              </div>
            </div>
          </div>
          {isLoading && (
            <div className='table-loading'>
              <Loading />
            </div>
          )}
          {!isLoading && total === 0 && (
            <div className='no_data'>
              <p> No data available </p>
            </div>
          )}

          <div className='table-list'>
            { !isLoading && results && _.map(results, (data, i) => this.renderRow(data, i)) }
          </div>
        </div>
        {/* Pagination */}
        {total > 0 && this.renderPagination()}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getAiStats: (opt) => dispatch(getAiStats(opt)),
    getAiPrompts: (opt) => dispatch(getAiPrompts(opt)),
  };
};

function mapStateToProps(state) {
  return {
    user: state.auth.user,
    isLoading: state.aiStats.isLoading,
    results: state.aiStats.results,
    page: state.aiStats.page,
    total: state.aiStats.total,
    prompts: state.aiTools.list,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AccountsStatsTable);
